import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { removeAccessRecruitmentToken, setAccessRecruitmentToken } from 'services/common/storage';
import {
  getProfileMemberRecruitmentsService,
  getRecruitmentsCareersService,
  getRecruitmentsDepartmentsService,
  getRecruitmentsFormsService,
  getRecruitmentsTitlesService,
  getRecruitmentsWorkplacesService,
  loginRecruitmentsMembersService,
  getCVUploadedProfileService,
  logoutRecruitmentsMembersService,
} from 'services/recruitment';
import {
  RecruitmentsCareersData,
  RecruitmentsDepartmentsData,
  RecruitmentsFormsData,
  RecruitmentsKeywordParams,
  RecruitmentsMembersLoginParamsTypes,
  RecruitmentsTitlesData,
  RecruitmentsWorkplacesData,
  LoginRecruitmentsMembers,
  LoginRecruitmentsMembersData,
  UploadedType,
} from 'services/recruitment/types';
import { LOCAL_STORAGE } from 'utils/constants';

interface SelectedRecruitment {
  id: number;
  label: string;
}

interface Recruitment {
  workplaces?: RecruitmentsWorkplacesData[];
  careers?: RecruitmentsCareersData[];
  forms?: RecruitmentsFormsData[];
  titles?: RecruitmentsTitlesData[];
  departments?: RecruitmentsDepartmentsData[];
  data?: LoginRecruitmentsMembers;
  tokenRecruitment?: string | null;
  cvUploaded?: UploadedType;
  selectedRecruitment?: SelectedRecruitment;
  useDefaultCv?: boolean;
}

const initialState: Recruitment = {
  workplaces: undefined,
  careers: undefined,
  forms: undefined,
  titles: undefined,
  departments: undefined,
  data: undefined,
  tokenRecruitment: window.sessionStorage.getItem(LOCAL_STORAGE.ACCESS_RECRUITMENT_TOKEN),
  cvUploaded: undefined,
  selectedRecruitment: undefined,
  useDefaultCv: undefined,
};

export const getRecruitmentsWorkplacesAction = createAsyncThunk<
  RecruitmentsWorkplacesData[],
  RecruitmentsKeywordParams,
  { rejectValue: ErrorResponse }
>(
  'recruitmentReducer/getRecruitmentsWorkplaces',
  async (params: RecruitmentsKeywordParams, { rejectWithValue }) => {
    try {
      return await getRecruitmentsWorkplacesService(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getRecruitmentsCareersAction = createAsyncThunk<
  RecruitmentsCareersData[],
  RecruitmentsKeywordParams,
  { rejectValue: ErrorResponse }
>(
  'recruitmentReducer/getRecruitmentsCareers',
  async (params: RecruitmentsKeywordParams, { rejectWithValue }) => {
    try {
      return await getRecruitmentsCareersService(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getRecruitmentsFormsAction = createAsyncThunk<
  RecruitmentsFormsData[],
  RecruitmentsKeywordParams,
  { rejectValue: ErrorResponse }
>(
  'recruitmentReducer/getRecruitmentsForms',
  async (params: RecruitmentsKeywordParams, { rejectWithValue }) => {
    try {
      return await getRecruitmentsFormsService(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getRecruitmentsTitlesAction = createAsyncThunk<
  RecruitmentsTitlesData[],
  RecruitmentsKeywordParams,
  { rejectValue: ErrorResponse }
>(
  'recruitmentReducer/getRecruitmentsTitles',
  async (params: RecruitmentsKeywordParams, { rejectWithValue }) => {
    try {
      return await getRecruitmentsTitlesService(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getRecruitmentsDepartmentsAction = createAsyncThunk<
  RecruitmentsDepartmentsData[],
  RecruitmentsKeywordParams,
  { rejectValue: ErrorResponse }
>(
  'recruitmentReducer/getRecruitmentsDepartments',
  async (params: RecruitmentsKeywordParams, { rejectWithValue }) => {
    try {
      return await getRecruitmentsDepartmentsService(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const loginRecruitmentsMembersAction = createAsyncThunk<
  LoginRecruitmentsMembersData,
  RecruitmentsMembersLoginParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'userReducer/loginRecruitmentsMembersAction',
  async (params, { rejectWithValue }) => {
    try {
      const response = await loginRecruitmentsMembersService(params);
      setAccessRecruitmentToken(response.token);
      return response;
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const logoutRecruitmentsMembersAction = createAsyncThunk<
  void,
  void,
  { rejectValue: ErrorResponse }
>(
  'userReducer/logoutRecruitmentsMembersAction',
  async (_, { rejectWithValue }) => {
    try {
      await logoutRecruitmentsMembersService();
      return;
    } catch (error) {
      throw rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getProfileMemberRecruitmentAction = createAsyncThunk<
  LoginRecruitmentsMembers,
  void,
  { rejectValue: ErrorResponse }
>(
  'userReducer/getProfileMemberRecruitmentAction',
  async (_, { rejectWithValue }) => {
    try {
      return await getProfileMemberRecruitmentsService();
    } catch (error) {
      const err = error as any;
      if (err && err.response.data.errors.length && err.response.data.errors[0].code === 401) {
        removeAccessRecruitmentToken();
      }
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getCVUploadedAction = createAsyncThunk<
    UploadedType
>('getCVUploadedProfileService', async (_, { rejectWithValue }) => {
  try {
    return await getCVUploadedProfileService();
  } catch (error) {
    return rejectWithValue(error as undefined);
  }
});

export const recruitmentSlice = createSlice({
  name: 'recruitmentReducer',
  initialState,
  reducers: {
    setSelectedRecruitment: ($state, action: PayloadAction<SelectedRecruitment>) => {
      $state.selectedRecruitment = action.payload;
    },
    setUseDefaultCv: ($state, action: PayloadAction<boolean>) => {
      $state.useDefaultCv = action.payload;
    },
    setProfileRecruitment: ($state, action: PayloadAction<LoginRecruitmentsMembers>) => {
      $state.data = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(
      getRecruitmentsWorkplacesAction.fulfilled,
      ($state, action) => {
        $state.workplaces = action.payload;
      },
    );
    builder.addCase(
      getRecruitmentsCareersAction.fulfilled,
      ($state, action) => {
        $state.careers = action.payload;
      },
    );
    builder.addCase(getRecruitmentsFormsAction.fulfilled, ($state, action) => {
      $state.forms = action.payload;
    });
    builder.addCase(getRecruitmentsTitlesAction.fulfilled, ($state, action) => {
      $state.titles = action.payload;
    });
    builder.addCase(
      getRecruitmentsDepartmentsAction.fulfilled,
      ($state, action) => {
        $state.departments = action.payload;
      },
    );
    builder.addCase(
      loginRecruitmentsMembersAction.fulfilled,
      ($state, action) => {
        $state.data = action.payload.member;
        $state.tokenRecruitment = action.payload.token;
      },
    );
    builder.addCase(
      logoutRecruitmentsMembersAction.fulfilled,
      ($state) => {
        $state.data = undefined;
        $state.workplaces = undefined;
        $state.careers = undefined;
        $state.forms = undefined;
        $state.titles = undefined;
        $state.departments = undefined;
        $state.data = undefined;
        $state.tokenRecruitment = null;
        $state.cvUploaded = undefined;
        removeAccessRecruitmentToken();
      },
    );
    builder.addCase(getProfileMemberRecruitmentAction.fulfilled, ($state, action) => {
      $state.data = action.payload;
    });
    builder.addCase(getCVUploadedAction.fulfilled, ($state, action) => {
      $state.cvUploaded = action.payload;
    });
  },
});

export const {
  setUseDefaultCv,
  setSelectedRecruitment,
  setProfileRecruitment,
} = recruitmentSlice.actions;

export default recruitmentSlice.reducer;
